import axios, { AxiosRequestConfig } from "axios";
import { APIResult, LINEAuthorizationResponse } from "../my-type";
import {
  callApi,
  configureApiClient,
} from "../api/client";
import { AuthService as GeneratedAuthService } from "../generated/api/services/AuthService";
import type { PasswordLoginRequest } from "../generated/api/models/PasswordLoginRequest";
import type { PasswordAccountCreateRequest } from "../generated/api/models/PasswordAccountCreateRequest";
import type { PasswordChangeRequest } from "../generated/api/models/PasswordChangeRequest";

const asApiResult = (value: unknown, fallbackMessage: string): APIResult => {
  if (value && typeof value === "object") {
    const response = value as { statusCode?: number; message?: string; data?: unknown };
    if (typeof response.statusCode === "number") {
      return {
        statusCode: response.statusCode,
        message: response.message ?? fallbackMessage,
        data: response.data,
      };
    }
  }

  return {
    statusCode: 1,
    message: "success",
    data: value,
  };
};

export const bindLineAccount = async (idToken: string, teamCode: string) => {
  try {
    configureApiClient();
    const data = await callApi(
      () => GeneratedAuthService.getApiAuthBindLineAccount(idToken, teamCode),
      "綁定 LINE 帳號失敗"
    );
    return data;
  } catch (error) {
    console.log(error);
    return null;
  }
};

export const getLINEAccessToken = async (
  code: string,
  redirect_uri: string,
  client_id: string,
  client_secret: string
) => {
  try {
    const grant_type = "authorization_code";
    const axiosConfig: AxiosRequestConfig = {
      method: "POST",
      url: "https://api.line.me/oauth2/v2.1/token",
      data: { grant_type, code, redirect_uri, client_id, client_secret },
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
    };

    const { data } = await axios(axiosConfig);
    return data as LINEAuthorizationResponse;
  } catch (error) {
    console.log(error);
    return null;
  }
};

export const checkLINEAccessTokenValidity = async (access_token: string) => {
  try {
    const axiosConfig: AxiosRequestConfig = {
      method: "GET",
      url: "https://api.line.me/oauth2/v2.1/verify",
      params: { access_token },
      headers: {
        "Content-Type": "application/json",
      },
    };

    await axios(axiosConfig);
    return true;
  } catch (error) {
    console.log(error);
    return false;
  }
};

export type PasswordLoginPayload = PasswordLoginRequest;

export const loginWithPassword = async (
  payload: PasswordLoginPayload
): Promise<APIResult | null> => {
  try {
    configureApiClient();
    const data = await callApi(
      () => GeneratedAuthService.postApiAuthPasswordLogin(payload),
      "登入失敗"
    );
    return asApiResult(data, "登入失敗");
  } catch (error) {
    console.log(error);
    return null;
  }
};

export const getCurrentUserProfile = async (token: string): Promise<APIResult | null> => {
  try {
    configureApiClient({
      tokenProvider: () => token,
    });

    const data = await callApi(
      () => GeneratedAuthService.getApiAuthMe(),
      "無法取得使用者資訊"
    );
    return asApiResult(data, "無法取得使用者資訊");
  } catch (error) {
    console.log(error);
    return null;
  }
};

export type RegisterAccountPayload = PasswordAccountCreateRequest;

export const registerAccount = async (
  payload: RegisterAccountPayload
): Promise<APIResult | null> => {
  try {
    configureApiClient();
    const data = await callApi(
      () => GeneratedAuthService.postApiAuthAccount(payload),
      "註冊失敗"
    );
    return asApiResult(data, "註冊失敗");
  } catch (error) {
    console.log(error);
    return null;
  }
};

export type ChangePasswordPayload = PasswordChangeRequest;

export const changePassword = async (
  payload: ChangePasswordPayload,
  token: string
): Promise<APIResult | null> => {
  try {
    configureApiClient({
      tokenProvider: () => token,
    });

    const data = await callApi(
      () => GeneratedAuthService.postApiAuthAccountChangePassword(payload),
      "變更密碼失敗"
    );
    return asApiResult(data, "變更密碼失敗");
  } catch (error) {
    console.log(error);
    return null;
  }
};

export const deleteAccount = async (token: string): Promise<APIResult | null> => {
  try {
    configureApiClient({
      tokenProvider: () => token,
    });

    const data = await callApi(
      () => GeneratedAuthService.deleteApiAuthMe(),
      "刪除帳號失敗"
    );
    return asApiResult(data, "刪除帳號失敗");
  } catch (error) {
    console.log(error);
    return null;
  }
};
